/**
 * LogStatCards.jsx — the five counters above the Ocean API Logs table.
 *
 * Fed by GET /api/ocean-query-log/stats, which aggregates over the indexed
 * `type` field under the same filters as the table — so the cards always
 * describe exactly the slice being viewed, not the whole collection.
 */
import React from "react";
import { formatNumber } from "./logUtils";

function StatCard({ label, value, dotColor, valueClass, highlight }) {
  return (
    <div
      className={`rounded-xl border px-5 py-4 ${
        highlight
          ? "bg-[#FFFBF0] border-[#F6E3B4] border-l-4 border-l-primary"
          : "bg-white border-gray-200"
      }`}
    >
      <div className="flex items-center gap-2">
        <span className="w-[7px] h-[7px] rounded-full" style={{ backgroundColor: dotColor }} />
        <span className="text-[13px] font-medium text-gray-500">{label}</span>
      </div>
      <p className={`mt-1 text-[34px] leading-[44px] font-semibold ${valueClass}`}>{formatNumber(value)}</p>
    </div>
  );
}

function LogStatCards({ stats }) {
  return (
    // A grid, not wrapping flex: with five cards a flex row stretched whichever
    // one wrapped to the full width. Every cell is one card wide instead.
    <div className="grid grid-cols-5 gap-4 lg:grid-cols-3 md:grid-cols-2 sm:grid-cols-1">
      {/* Each card is its count alone. What's behind it — the failed tally, the
          endpoint each type calls, the new/update split inside Bookings — is
          either on the rows themselves or in the User-wise Count tab. */}
      <StatCard label="Total logs" value={stats.total} dotColor="#FCB614" valueClass="text-primary" highlight />
      <StatCard label="Rate search" value={stats.rate_search} dotColor="#3B82F6" valueClass="text-[#1E293B]" />
      {/* The create-booking call (/create-ocean-booking, /kam/ocean-booking),
          which only ever leaves the query awaiting the KAM's confirmation. */}
      <StatCard
        label="Confirmation awaited"
        value={stats.confirmation_awaited}
        dotColor="#F59E0B"
        valueClass="text-[#1E293B]"
      />
      {/* /api/kam/ocean-booking/confirm — the MBL number going in, which is what
          books the shipment. Booking updates are counted here too. */}
      <StatCard label="Bookings" value={stats.bookings} dotColor="#22C55E" valueClass="text-[#1E293B]" />
      {/* /deletebooking is a generic air + ocean cancel, but only the ocean ones
          are logged (isOceanPayment in userController.deleteBooking), so this
          counts ocean cancellations — rejected ones included. */}
      <StatCard label="Booking cancel" value={stats.booking_cancel} dotColor="#EF4444" valueClass="text-[#1E293B]" />
    </div>
  );
}

export default LogStatCards;
